import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface LocaleVariables {
  [key: string]: string | number;
}

export interface LocaleFile {
  [key: string]: string;
}

export interface LocalesState {
  currentLocale: string;
  locales: Record<string, LocaleFile>; // locale_file -> { key: translation }
  loading: Record<string, boolean>; // locale_file -> loading state
}

const isStandalone = process.env.REACT_APP_TYPE === 'standalone';
const fallbackLocale = isStandalone ? 'en' : 'de';

const getInitialLocale = (): string => {
  if (typeof window === 'undefined') {
    return fallbackLocale;
  }

  try {
    const savedLocale = localStorage.getItem('locale');

    return savedLocale === 'en' || savedLocale === 'de'
      ? savedLocale
      : fallbackLocale;
  } catch {
    return fallbackLocale;
  }
};

const initialState: LocalesState = {
  currentLocale: getInitialLocale(),
  locales: {},
  loading: {},
};

const localesSlice = createSlice({
  name: 'locales',
  initialState,
  reducers: {
    setCurrentLocale: (state, action: PayloadAction<string>) => {
      state.currentLocale = action.payload;
    },
    setLocaleFile: (
      state,
      action: PayloadAction<{
        locale: string;
        file: string;
        translations: LocaleFile;
      }>
    ) => {
      const { locale, file, translations } = action.payload;
      const key = `${locale}_${file}`;
      state.locales[key] = translations;
      state.loading[key] = false;
    },
    setLocaleFileLoading: (
      state,
      action: PayloadAction<{
        locale: string;
        file: string;
        loading: boolean;
      }>
    ) => {
      const { locale, file, loading } = action.payload;
      const key = `${locale}_${file}`;
      state.loading[key] = loading;
    },
    clearAllTranslations: state => {
      state.locales = {};
      state.loading = {};
    },
  },
});

export const {
  setCurrentLocale,
  setLocaleFile,
  setLocaleFileLoading,
  clearAllTranslations,
} = localesSlice.actions;

export default localesSlice.reducer;
